'use server';

import { ai } from '@/ai/genkit';
import { z } from 'genkit';

const RecommendationsInputSchema = z.object({
    historyString: z.string(),
});
export type RecommendationsInput = z.infer<typeof RecommendationsInputSchema>;

const RecommendationsOutputSchema = z.object({
    greeting: z.string(),
    advice: z.string(),
    suggestedSearch: z.string(),
});
export type RecommendationsOutput = z.infer<typeof RecommendationsOutputSchema>;

const FALLBACK_RECOMMENDATIONS: RecommendationsOutput = {
    greeting: "مرحباً بك في مكنة!",
    advice: "اكتشف أحدث السيارات وقطع الغيار الأصلية في منصتنا.",
    suggestedSearch: "أحدث السيارات"
};

// In-memory quota guard — avoid hammering the API after rate-limit
let quotaBlockedUntil = 0;

// Simple TTL cache: reuse last result for 10 min if history didn't change
const _cache = new Map<string, { result: RecommendationsOutput; expiresAt: number }>();
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

const recommendationsPrompt = ai.definePrompt({
    name: 'personalizedRecommendationsPrompt',
    input: { schema: RecommendationsInputSchema },
    output: { schema: RecommendationsOutputSchema },
    // Trimmed prompt — removed repetitive instructions, kept core intent
    prompt: `مستشار سيارات ذكي. سجل المستخدم: {{{historyString}}}
قدّم:
1. تحية قصيرة مخصصة.
2. نصيحة مفيدة واحدة مبنية على السجل.
3. اقتراح بحث (جملة قصيرة).
اكتب بالعربية فقط. كن موجزاً جداً.`,
    config: { maxOutputTokens: 200 }, // hard cap on output size
});

export async function getPersonalizedRecommendations(input: RecommendationsInput): Promise<RecommendationsOutput> {
    // If quota is blocked, return fallback immediately without calling API
    if (Date.now() < quotaBlockedUntil) return FALLBACK_RECOMMENDATIONS;

    // Return cached result if valid
    const cached = _cache.get(input.historyString);
    if (cached && Date.now() < cached.expiresAt) return cached.result;

    try {
        const { output } = await recommendationsPrompt(input);
        const result = output ?? FALLBACK_RECOMMENDATIONS;
        _cache.set(input.historyString, { result, expiresAt: Date.now() + CACHE_TTL_MS });
        return result;
    } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        if (msg.toLowerCase().includes('quota') || msg.includes('429')) {
            const match = msg.match(/retry in\s+([0-9.]+)s/i);
            const wait = Math.max(60_000, Math.ceil(Number(match?.[1] || 60) * 1000));
            quotaBlockedUntil = Date.now() + wait;
        }
        return FALLBACK_RECOMMENDATIONS;
    }
}
